export const en = {
  notifications: {
    eventCreated: 'Event <span class="bold">{{subject}}</span> has been created',
    showTheEvent: 'Show the event',
    eventCreationStarted: 'Event is being created...',
    eventResourceValidationInProgress: 'Event is being verified by resources...',
    eventAcceptedByResources: 'Event was accepted',
    eventRejectedByResources: 'Event was rejected',
  },
  availability: {
    tentative: 'Tentative',
    free: 'Free',
    reserved: 'Reserved',
    closed: 'Closed',
    freeClosed: 'Free (Closed)',
    reservedClosed: 'Reserved (Closed)',
    notReservableNow: 'Not reservable now',
  },
  scheduler: {
    showOnlyThisCalendar: 'Show only this calendar',
    showAllCalendars: 'Show all calendars',
    deleteCalendar: 'Delete calendar',
    calendars: 'Calendars',
    myCalendars: 'My calendars',
    primaryCalendar: 'Private calendar',
    otherUsers: 'Other users',
    resources: 'Locations',
    addCalendar: 'Add calendar',
    cancel: 'Cancel',
    placeholderSearch: 'Search by name, e-mail...',
    list: 'List',
    calendar: 'Calendar',
    view: 'View',
    allDay: 'All day',
    today: 'Today',
    next: 'Next',
    previous: 'Previous',
    dayView: 'Day',
    monthView: 'Month',
    weekView: 'Week',
    openCalendar: 'Open calendar',
    parentView: 'Parent view',
    resetFilters: 'Reset filters',
    selectAll: 'Add all to selection',
    selectNone: 'Remove all from selection',
    filtersApplied: 'Filters applied',
    availableResources: 'Available resources',
    currentSelection: 'Current selection',
    add: 'Add',
    remove: 'Remove',
    noSelectedResources: 'No resources selected',
    zoomIn: 'Zoom in',
    zoomOut: 'Zoom out',
    morning: 'Morning',
    afternoon: 'Afternoon',
    night: 'Night',
    noSubject: '(No subject)',
    selectAtLeastOneResourceTip: 'Select at least one resource to load events.',
    failedToLoadEvents: 'Failed to load events.',
  },
  schedulerSettings: {
    displayEyebrow: 'Display',
    displayTitle:
      'Adjust the calendar view for a clearer overview of your reservations and choose the default view when you enter the calendar.',
    showWorkingHours: 'Show working hours schedule',
    distinguishColumns: 'Distinguish columns visually',
    viewModeLabel: 'Calendar type',
    viewModeHint: 'Default calendar type when opening the calendar for the first time',
    dayView: 'Day',
    weekView: 'Week',
    monthView: 'Month',
    rowSizingModeLabel: 'Day size',
    rowSizingActualTitle: 'Real event size',
    rowSizingActualDescription:
      'Prefer displaying events in their real size based on duration, even if that means the title and time are not always fully visible.',
    rowSizingAutoTitle: 'Auto scale',
    rowSizingAutoDescription:
      'Automatically stretch parts of the day based on the number and length of events so the full title and time remain visible.',
    timelineEyebrow: 'Timeline',
    timelineTitle: 'Day segmentation',
    presetsEyebrow: 'Presets',
    presetsTitle: 'Quick day segmentation setup',
    previewEyebrow: 'Preview',
    previewTitle: 'Day segmentation into individual parts',
    resizeBoundaryAriaLabel: 'Resize the boundary for {{label}}',
    earlyMorning: 'Early morning',
    presetDefaultLabel: 'Simplified',
    presetDefaultDescription: 'Designed for a typical afternoon rhythm from lunch to the end of the workday',
    presetEarlyLabel: 'Standard day',
    presetEarlyDescription: 'Morning starts at seven, afternoon after lunch, and evening starts at six',
    presetOfficeLabel: 'Workday',
    presetOfficeDescription: 'Standard business segmentation',
    morningDescription:
      'The first part of the day with the highest concentration of meetings, especially short ones, often with a lunch break in the middle',
    afternoonDescription:
      'The second part of the day, often with fewer meetings than in the morning, but longer ones and a higher concentration in the first half of the afternoon',
    nightDescription: 'Evening and night',
  },
  calendarsView: {
    creationSubtitle: 'Start with the quick draft, then expand into the full reservation editor.',
    openReservationEditor: 'Open reservation editor',
    quickReserve: 'Quick reserve',
    settingsSubtitle: 'Tune the calendar display, row sizing and daily segmentation.',
    settingsSubtitleWithContext:
      'Tune how {{contextLabel}} is displayed and how each day is segmented in the calendar.',
  },
  workingHours: {
    open: 'Open',
    openEvent: 'Open when the event starts',
    alwaysClosed: 'Always closed',
    closed: 'Closed',
    closedEvent: 'Closed when the event starts',
    whenOpens: '(opens in <b>{{hours}} hours, {{minutes}} minutes</b>)',
    whenOpensEvent: '(opens <b>{{hours}} hours, {{minutes}} minutes</b> later)',
    monday: 'Monday',
    tuesday: 'Tuesday',
    wednesday: 'Wednesday',
    thursday: 'Thursday',
    friday: 'Friday',
    saturday: 'Saturday',
    sunday: 'Sunday',
    differentTimezone: 'You are in different timezone. Open hours are based on timezone of the resource.',
    locationTimezone: 'Location timezone:',
  },
  settings: {
    locale: {
      title: 'Language and localization',
      limitedTitle: 'Localization',
      firstDayOfWeek: 'First day of the week',
    },
    locations: {
      title: 'Home area and locations filtering',
      description: 'Settings for filtering.',
      homeArea: 'Home area',
      homeAreaDescription:
        'If you have a favourite area, such as a building or floor, you can set it as the default for filtering.',
      filteredAreas: 'Filtered areas',
      filteredAreasDescription:
        'Only locations that are located in these areas will be offered in reservation search results',
      save: 'Save',
    },
  },
  account: {
    exchangeUnavailable:
      'Could not connect to the Exchange server. Try again later. If the problem persists, contact your administrator and provide the error code and request ID.',
    exchangeAccountNotFound:
      'No Exchange account was found in CUE Solution for this username. Check your username. If it is correct, ask your administrator to add or link your Exchange account.',
    exchangeCredentialsRejected:
      'The Exchange server rejected your credentials. Check your Exchange username and password and try again.',
    exchangeAccountDeactivated:
      'Login to your CUE Solution account is deactivated. Contact your administrator to restore access.',
    exchangeConfigurationError:
      'Exchange login is not configured correctly. Contact your administrator and provide the error code and request ID.',
    exchangeAccountAmbiguous:
      'Login could not be completed because the account assignment is ambiguous. Contact your administrator and provide the error code and request ID.',
    exchangeInternalError:
      'Login could not be completed due to an application error. Try again later. If the problem persists, contact your administrator and provide the error code and request ID if shown.',
    exchangeApiUnavailable:
      'Could not connect to CUE Solution. Check your network connection and try again. If the problem persists, contact your administrator.',
    loginErrorCode: 'Error code',
    loginRequestId: 'Request ID',
    unsuccessfulLogin: 'Login was unsuccessful',
    wrongCreddentials: 'Wrong credentials - check your username and password. User may be deactivated.',
    unknownError: 'Unknown error - please try again later',
    accountLocked: 'Account is temporarily locked.',
    requiredField: 'This field is mandatory.',
    passwordValidation:
      'Password has to be from 12 to 128 characters long. Password requires at least 3 out of 4 groups (uppercase, lowercase, number, special character) and no more than 2 equal characters in a row.',
    passwordMismatch: 'New password and confirmation do not match.',
    passwordInHistory: 'This password was used before. Please choose a different password.',
    featureNotAvailableYet: 'This feature is not available yet.',
  },
  findCol: {
    find: 'Search the colleague you are willing to reserve a location next to:',
    hint: "Please use search above to show the colleague's events",
  },
  qr: {
    unrecognizedTitle: 'Unrecognized QR code',
    unrecognizedBody: 'We cannot recognize the QR code. Are you sure it belongs to the CUE Solution?',
    text: 'Scan QR code to open reservation, or to confirm your reservation',
    shame: 'Error occurs, please try the operation again later.',
    canceled: 'Your reservation was cancelled successfully',
    confirm: 'Confirm your reservation',
    confirmDelete: 'Are you sure you want to Cancel your reservation?',
    confirmDeleteDialog: 'This reservation will be canceled.',
    confirmed: 'Your reservation is confirmed now.',
    confirmFail: 'Unable to confirm the event, please contact the reservation system administrator.',
    detail: 'Reservation detail',
  },
  leftMenu: {
    duration15: 'Minimal duration is 15 minutes',
    optional: 'Optional',
    remotely: 'Remotely',
    attendees: 'Attendees',
    addMe: 'Add me',
    accept: 'Set',
    cancel: 'Cancel',
    date: 'Date',
    time: 'Time',
    today: 'Today',
    filter: 'Filter',
    from: 'From',
    to: 'To',
    duration: 'Required duration',
    name: 'Name',
    searchByName: 'Location name',
    foundIn: 'Found in',
    area: 'Area',
    type: 'Type',
    capacity: 'Capacity',
    reset: 'Reset',
    group: 'Group',
    closeFilters: 'Close filters',
    openFilters: 'Open filters',
  },
  dropdowns: {
    attendeesHint: 'Search by name or email address.',
  },
  resourceDetail: {
    defaultTitle: 'Reservation in {resource-display-name} from {start-date} {start-time} to {end-date} {end-time}.',
    pleaseJoinMe:
      'Hello, here is an invitation to the meeting in {resource-display-name} from {start-date} {start-time} to {end-date} {end-time}.',
    cancel: 'Cancel',
    capacity: 'Capacity',
    type: 'Type',
    name: 'Name',
    qrCategoryName: 'Category',
    qrCode: 'Code',
  },
  unitDetail: {
    cueDevice: 'CUE device',
    magicName: 'Magic name',
    status: 'Status',
    lastActive: 'Last active',
    serialNumber: 'Serial number',
    firmware: 'Firmware',
    ipAddress: 'IP address',
    screenshot: 'No-interactive screenshot',
    lastDownloaded: 'Last downloaded screenshot',
    online: 'Online',
    offline: 'Offline',
  },
  secondMenu: {
    foundResourceByName: 'Found locations',
    group: 'Group',
    capacity: 'Capacity',
    occupancy: 'Occupancy',
    name: 'Name',
    ascending: 'ascending',
    descending: 'descending',
  },
  common: {
    saved: 'Saved successfully',
    releaseFailed: 'Release for non-confirmation failed',
    unlimited: 'Unlimited',
    finished: 'Finished',
    released: 'Released for non-confirmation',
    confirmed: 'Confirmed',
    needConfirmation: 'Need confirmation',
    confirmNow: 'Confirm now',
    searchNotFound: 'No data found',
    contactToCheckAccount: 'Plese contact your system administrator to check your account settings.',
    internalError: 'Internal server error - please contact your server administrator',
    notFoundResource: 'Location has not been found.',
    notFoundReservation: 'Reservation has not been found.',
    available: 'Available',
    availableOutsideWorkingHours: 'Outside working hours',
    reserved: 'Reserved',
    closed: 'Closed',
    limited: 'Limited',
    assignedToYou: 'Yours',
    assignedToOthers: 'Others',
    accepted: 'Accepted',
    cancelled: 'Cancelled',
    unconfirmed: 'Unconfirmed',
    declined: 'Declined',
    tentative: 'Tentative',
    refresh: 'Refresh',
    reserve: 'Reserve',
    starts: 'starts',
    ends: 'ends',
    start: 'start',
    reservations: 'Reservations',
    not_available: 'Not available',
    onLater: 'soon',
    now: 'now',
    quickReserve: 'Favourite',
    shortDurationUnits: {
      days: 'd',
      hours: 'h',
      minutes: 'min',
    },
    placeholders:
      'You can use placeholders: {resource-url} {resource-display-name} {start-date} {start-time} {end-date} {end-time} {resource-capacity} {resource-type} {attendees-count}. They will be replaced for real data of event.',
    cancelledUnconfirmed: 'Cancelled - Unconfirmed',
    reloadIn: 'Reload in',
    serverDownTimer: 'Server in unavailable',
    somethingWentWrong: 'Something went wrong',
    common404Error: 'Error 404 - Not Found',
    noReservationObject: 'No QR code or resource is connected.',
  },
  menu: {
    monitorControl: 'Monitor & Control',
    analytics: 'Analytics',
    analyticsReservation: 'Reservation',
    analyticsNavigation: 'Navigation',
    analyticsMonitorAndControls: 'Monitor & Control',
    analyticsSatisfactionRatings: 'Satisfaction ratings',
    news: 'News',
    dashboard: 'Home',
    reserve: 'Reservation',
    calendar: 'Calendar',
    timeline: 'Timeline',
    floorplans: 'Floorplans',
    findFindColleague: 'Find a colleague',
    reservations: 'All reservations',
    calendarsView: 'Locations',
    locations: 'Locations',
    navigation: 'Navigate',
    settings: 'Settings',
    profile: 'Profile',
    pinMenu: 'Pin menu',
    unpinMenu: 'Unpin menu',
    orders: 'My orders',
    logout: 'Log Out',
    reserveButton: 'New reservation',
    ordersandservices: 'My orders and requests',
  },
  login: {
    or: 'or',
    linkedAccount: 'Linked account',
    pairViaEmailLogin: 'Pair via e-mail login',
    notPaired: 'There is no internal CUE Solution user account for this account.',
    goToNavigationApp: 'Navigation app',
    newsError: 'Could not load the news',
    subtitle: 'The smart buildings solution for your business, reinvented',
    welcome: 'Welcome back!',
    loginWith: 'Login with',
    language: 'Language',
    copyright: 'Copyright',
    producedBy: 'Produced by',
    continue: 'Continue',
    password: 'Password',
    loginViaPassword: 'Login',
    loginViaEmail: 'Login',
    loginViaEmailLink: 'Login with e-mail',
    forgotPassword: 'Forgot password?',
    resetPasswordTitle: 'Reset password',
    resetPasswordDescription: 'Enter your e-mail address for password reset.',
    sendPasswordReset: 'Send reset link',
    resetPasswordRequestSent: 'If the account exists, a password reset e-mail was sent.',
    setNewPasswordTitle: 'Set new password',
    setNewPasswordDescription: 'Enter your new password.',
    resetPasswordConfirm: 'Confirm new password',
    resetPasswordSuccess: 'Password has been changed successfully.',
    emailLoginTitle: 'Login via e-mail - one time code',
    emailLoginSessionCode:
      'If the e-mail address you had entered in the previous step was valid, an e-mail containing the one-time code has been send to that address',
    accessCodeSent: 'We have sent you the access code to your e-mail',
    requestId: 'RequestID',
    codeFromEmail: 'Code from e-mail',
    missingRequestId: 'Missing parameter - please start again by pressing the button below',
    backToLogin: 'Back to login',
    back: 'Back',
    notReceivedEmail:
      "Didn't receive an email? Please check your email address or <a href='#' class='go-back'>go back</a> and try to send the email again.",
    passwordSetupHint:
      'You can now also sign in with a password. If you want to set a password, sign in and open the "Security" tab in Profile.',
    version: 'Version',
  },
  deletePopup: {
    title: 'Delete event',
    question: 'Do you really want to delete event',
    questionOccurrence: 'Do you really want to delete occurrence of event',
    questionSeries: 'Do you really want to delete whole series of event',
    delete: 'Delete',
    close: 'Close',
  },
  cancelParticipationPopup: {
    title: 'Cancel participation on the event',
    question: 'Do you really want to cancel your participation on the event',
    cancel: 'Cancel participation',
    close: 'Close',
  },
  newReservationUi: {
    modalTitle: {
      new: 'Create reservation',
      edit: 'Edit reservation',
      detail: 'Reservation detail',
    },
    state: {
      loading: 'Loading reservation details...',
      errorTitle: 'Reservation cannot be opened',
      missingIdentifiers: 'The dialog was opened without the identifiers required to load reservation details.',
      loadFailed: 'Reservation details could not be loaded.',
      permissionDenied:
        'Reservation details could not be loaded. You probably do not have permission to view this event.',
      saveFailed: 'Reservation could not be completed.',
      resourcesUnavailable: 'Reservation resources could not be resolved.',
      createTitle: 'Create reservation',
      editTitle: 'Edit reservation',
    },
    sections: {
      title: 'Title',
      timeSettings: 'Time settings',
      locationAndOnline: 'Location and online',
      description: 'Description',
      administrationAndAttendees: 'Administration and attendees',
      locationDetailAndOrders: 'Location detail and orders',
    },
    title: {
      subjectPlaceholder: 'Reservation in {resource-display-name}',
      subjectHelp: 'This name will be a title of your reservation and subject of the e-mail.',
      placeholderHelp: 'You can use placeholders: {{placeholders}}. They will be replaced with reservation data.',
      subjectRequired: 'Title is required.',
    },
    description: {
      placeholderHelp: 'You can use placeholders: {{placeholders}}. They will be replaced with reservation data.',
      clear: 'Clear description',
      emptyReadonly: 'No description.',
    },
    time: {
      allDayEvent: 'All day event',
      start: 'Start',
      end: 'End',
      recurrence: 'Recurrence',
      editWholeSeries: 'Edit whole series',
      recurrenceConfigured: 'Recurrence is configured for this reservation.',
    },
    administration: {
      showAsLabel: 'Show the event in my calendar as',
      showAsFree: 'Free',
      showAsTentative: 'Tentative',
      showAsBusy: 'Busy',
      showAsWorkingElsewhere: 'Working elsewhere',
      showAsAway: 'Away',
      organizer: 'Organizer',
      attendees: 'Additional attendees',
      noAttendees: 'No attendees selected.',
      addAttendee: 'Add an attendee',
      addEmailAttendee: 'Add {{email}}',
      required: 'Required',
      optional: 'Optional',
      removeAttendee: 'Remove',
      availabilityUnknown: 'Availability not checked',
      availabilityAvailable: 'Available',
      availabilityWarning: 'Warning',
      availabilityBusy: 'Busy',
      availabilityCouldNotBeChecked: 'Attendee availability could not be checked.',
      availabilityTentative: 'Attendee availability is tentative.',
      availabilityBusyReason: 'The attendee is busy for the selected time.',
      responseAccepted: 'Accepted',
      responseRejected: 'Rejected',
      responseNone: 'No response',
      responseTentative: 'Tentative',
    },
    location: {
      noLocationSelected: 'No location selected yet.',
      addAnotherLocation: 'Add another location',
      resourceSearchPlaceholder: 'Search locations...',
      noResourcesFound: 'No matching locations found.',
      resourcePickerLoading: 'Checking availability...',
      resourceCapacity: 'Capacity: {{count}}',
      availabilityUnknown: 'Availability not checked',
      availabilityAvailable: 'Available',
      availabilityWarning: 'Warning',
      availabilityError: 'Cannot reserve',
      availabilityCouldNotBeChecked: 'Availability could not be checked.',
      availabilityTentative: 'Availability is tentative.',
      availabilityBlocked: 'This location is not available for the selected time.',
      multipleResourceTypeReservationsNotAllowed:
        'This location type does not allow multiple simultaneous reservations, and another location of this type is already reserved.',
      reservationsDisabled: 'Reservations are disabled for this location.',
      resourceDeclined: 'The location declined this reservation.',
      resourceNeedsConfirmation: 'The location requires confirmation.',
      reservationRules: 'Reservation rules',
      changeLocation: 'Change',
      removeLocation: 'Remove',
      onlineMeeting: 'Online meeting',
      onlineMeetingNone: 'No online meeting',
      onlineMeetingTeams: 'Microsoft Teams',
      onlineMeetingMeet: 'Google Meet',
      onlineMeetingWebex: 'Webex',
    },
    locationDetails: {
      noLocationSelected: 'Select a location to show its details and orders.',
      orders: 'Orders',
      previousOrders: 'Existing orders',
      noOrders: 'No orders for this location.',
      newOrder: 'New order',
      newOrderInstruction: 'Add an order item to create an order for this location.',
      noOrderServices: 'This location does not support orders, or they are currently unavailable.',
      orderNote: 'Note to order',
      itemNote: 'Item note',
      noItemNote: 'No note.',
      addOrderItem: 'Add order item',
      removeOrderItem: 'Remove order item',
      noteMinLength: 'Note must have at least {{length}} characters.',
    },
    save: {
      disableNoResourceSelected: 'Select at least one location before reserving.',
      invalidTimeRange: 'End must be after start.',
      disablePast: 'It is not possible to reserve for a time period that is entirely in the past.',
      disableMinimumDurationForResource: 'Minimum reservation duration in {{resourceName}} is {{duration}}.',
      disableMaximumDurationForResource: 'Maximum reservation duration in {{resourceName}} is {{duration}}.',
      disableAvailabilityForResource: '{{resourceName}} is not available for a part of the selected time range.',
      recurringConflictsDetected: 'Recurring event conflicts detected ({{conflicted}}/{{total}}).',
      recurringMeetingsNotAllowedForResource: 'Recurring meetings are not allowed for {{resourceName}}.',
      recurringEventOccurrencesOverlap:
        'Recurring event instances overlap. Shorten duration or increase repeat interval.',
      recurringEventRequiresEndDateWithinDays:
        'Recurring event for {{resourceName}} requires an end date within {{days}} days.',
      recurringEventExceedsBookingWindow:
        'Recurring event for {{resourceName}} exceeds booking window ({{days}} days).',
      recurringEventRequiresEndWithinOccurrences:
        'Recurring event for {{resourceName}} requires an end within {{occurrences}} occurrences.',
      recurringEventExceedsMaxOccurrences:
        'Recurring event for {{resourceName}} exceeds the maximum number of occurrences ({{occurrences}}).',
      recurringEventScopeoutAppliedDays: 'Recurring event exceeds limit and will be scoped to {{days}} days.',
      recurringEventScopeoutAppliedOccurrences:
        'Recurring event exceeds limit and will be scoped to {{occurrences}} occurrences.',
      recurringEventOverLimitIgnoredDaysWarning:
        'Recurring event exceeds {{days}} days and may be rejected by the resource provider.',
      recurringEventOverLimitIgnoredOccurrencesWarning:
        'Recurring event exceeds {{occurrences}} occurrences and may be rejected by the resource provider.',
      repetitiveAvailabilityCheckFailed: 'Recurring availability could not be checked.',
    },
    actions: {
      close: 'Close',
      edit: 'Edit',
      save: 'Reserve',
      cancelParticipation: 'Cancel participation',
      deleteEvent: 'Delete event',
      deleteOccurrence: 'Delete occurrence',
      deleteSeries: 'Delete series',
    },
    deleteConfirmation: {
      title: 'Delete reservation',
      questionEvent: 'Do you really want to delete reservation',
      questionOccurrence: 'Do you really want to delete this occurrence of reservation',
      questionSeries: 'Do you really want to delete the whole reservation series',
    },
    plannedSections: 'Planned reservation sections',
  },
  reservationPopup: {
    addMeToReservation: 'Add me to the reservation',
    copied: 'Copied to clipboard',
    visibility: {
      title: 'Visibility of the event',
      public: 'Public',
      private: 'Private',
    },
    eventShow: 'Show event in calendar as:',
    free: 'Free',
    workingElsewhere: 'Working elsewhere',
    tentative: 'Tentative',
    busy: 'Busy',
    away: 'Away',
    addToFavourite: 'Add to favourite',
    removeFromFavourites: 'Remove from favourite',
    title: 'Reservation detail',
    location: 'Location',
    name: 'Title',
    description: 'Description',
    onlineMeeting: 'Online meeting',
    duration: 'Duration',
    start: 'Start',
    allDay: 'All day',
    end: 'End',
    attendees: 'Attendees',
    optionalAttendees: 'Optional attendees',
    requiredAttendees: 'Required attendees',
    organizer: 'Organizer',
    close: 'Close',
    reservation: 'Reservation',
    detail: 'Location detail',
    change: 'change',
    other: 'Unfortunately, it is not possible to reserve this location as it is assigned to other user(s).',
    none: 'None',
    navigationHint: 'Navigation support link',
    navigationHintDescription:
      'A link to navigate to the meeting location can be automatically added to the event description. It can lead to the anonymous CUE Navigation application, the internal CUE Assist application available after login, or to both applications.',
    both: 'Both Apps',
    newReservation: 'New reservation',
    eventType: 'Event type',
    editWholeSeries: 'Edit whole series',
    bookingRulesLabel: 'Reservation rules',
    seriesScopeLabel: 'Series',
    deleteOccurrence: 'Delete occurrence',
    deleteSeries: 'Delete series',
    schedulingValidationRules: 'Scheduling validation rules',
    recurrenceTypeSingleInstance: 'Single instance',
    recurrenceTypeSeriesMaster: 'Series master',
    recurrenceTypeSeries: 'Series',
    recurrenceTypeOccurrence: 'Occurrence',
    recurrenceTypeException: 'Exception',
    recurrenceSummaryEveryPattern: 'Every {{pattern}}',
    recurrenceSummaryEveryPatternInterval: 'Every {{interval}} {{pattern}}',
    recurrenceSummaryOccurrences: '{{repeatInfo}}, {{occurrences}} occurrences',
    recurrenceSummaryUntil: '{{repeatInfo}}, until {{endDate}}',
    recurrenceSummaryNoEnd: '{{repeatInfo}}, no end date',
    recurrencePattern: {
      daily: 'daily',
      weekly: 'weekly',
      absoluteMonthly: 'monthly',
      relativeMonthly: 'monthly',
      absoluteYearly: 'yearly',
      relativeYearly: 'yearly',
    },
    recurrenceEditorMessages: {
      repeat: 'Repeat',
      dailyInterval: 'day(s)',
      dailyRepeatEvery: 'Repeat every',
      weeklyInterval: 'week(s)',
      weeklyRepeatEvery: 'Repeat every',
      weeklyRepeatOn: 'Repeat on',
      monthlyDay: 'Day',
      monthlyInterval: 'month(s)',
      monthlyRepeatEvery: 'Repeat every',
      monthlyRepeatOn: 'Repeat on',
      yearlyOf: 'of',
      yearlyRepeatEvery: 'Repeat every',
      yearlyRepeatOn: 'Repeat on',
      yearlyInterval: 'year(s)',
      frequenciesDaily: 'Daily',
      frequenciesMonthly: 'Monthly',
      frequenciesNever: 'Never',
      frequenciesWeekly: 'Weekly',
      frequenciesYearly: 'Yearly',
      offsetPositionsFirst: 'First',
      offsetPositionsSecond: 'Second',
      offsetPositionsThird: 'Third',
      offsetPositionsFourth: 'Fourth',
      offsetPositionsLast: 'Last',
      weekdaysDay: 'Day',
      weekdaysWeekday: 'Weekday',
      weekdaysWeekendday: 'Weekend day',
      endAfter: 'After',
      endOccurrence: 'occurrence(s)',
      endLabel: 'End',
      endNever: 'Never',
      endOn: 'On',
      numericIncrement: 'Increment value',
      numericDecrement: 'Decrement value',
      dateInputsToday: 'Today',
      dateInputsToggle: 'Toggle calendar',
      dateInputsParentViewButton: 'Parent view',
    },
    schedulingRuleMinDuration: 'Min duration: {{duration}}',
    schedulingRuleMaxDuration: 'Max duration: {{duration}}',
    schedulingRuleMaxAdvanceBooking: 'Max advance booking: {{days}} days',
    schedulingRuleRecurringMaxAdvanceBooking: 'Recurring max advance booking: {{days}} days',
    schedulingRuleRecurringMaxOccurrences: 'Recurring max occurrences: {{occurrences}}',
    schedulingRuleOutsideWorkingHoursRejected: 'Outside working hours is rejected',
    schedulingRuleRecurringMeetingsAllowed: 'Recurring meetings: allowed',
    schedulingRuleRecurringMeetingsNotAllowed: 'Recurring meetings: not allowed',
    schedulingRuleRecurringBookingWindow: 'Recurring booking window: {{days}} days',
    schedulingRuleRecurringHorizonEnforcementEnabled: 'Recurring horizon enforcement: enabled',
    schedulingRuleRecurringHorizonEnforcementDisabled: 'Recurring horizon enforcement: disabled',
    schedulingRuleConflictsAllowed: 'Conflicts: allowed',
    schedulingRuleConflictsNotAllowed: 'Conflicts: not allowed',
    schedulingRuleMaxConflictInstances: 'Max conflict instances: {{count}}',
    schedulingRuleMaxConflictPercentage: 'Max conflict percentage: {{percentage}}%',
    reminder: {
      title: 'Remind event',
      dontRemind: 'Do not remind',
      justInTime: 'At time of event',
      fiveMinutes: '5 minutes before',
      tenMinutes: '10 minutes before',
      fifteenMinutes: '15 minutes before',
      thirtyMinutes: '30 minutes before',
      oneHour: '1 hour before',
      twoHours: '2 hours before',
      twelveHours: '12 hours before',
      oneDay: '1 day before',
      oneWeek: '1 week before',
      customMinutesBefore: '{{minutes}} minutes before',
    },
  },
  dashboard: {
    quickReserveImageUrl: '/assets/images/favs-hint_en.png',
    showOnFloorplan: 'Show on floorplan',
    quickReserveExplain:
      'If you wish to be able to reserve quickly, add any room or other location to your favourites in the New reservation window - Location detail',
    confirm: 'Confirm',
    noEvents: 'You have no reservation',
    eventDetail: 'event detail',
    deleteEvent: 'Delete event',
    logout: 'logout',
    myReservations: 'Reservations',
    cancelParticipation: 'Cancel participation',
  },
  finishEvent: {
    finish: 'Finish',
    finishEvent: 'Finish the event',
    question: 'Are you sure you want to finish this event? The event will be completed within the next few minutes.',
  },
  reservations: {
    errorCreateEvent: 'Error creating event',
    organiserAndAttendees: 'Organizer & attendees',
    disableMinimumDuration: 'It is required to reserve this resource at least for {{duration}}',
    disableMinimumDurationForResource: 'Minimum reservation duration in {{resourceName}} is {{duration}}',
    disableMaximumDuration: 'It is required to reserve this resource at most for {{duration}}',
    disableMaximumDurationForResource: 'Maximum reservation duration in {{resourceName}} is {{duration}}',
    disableAvailability: 'Location is not available for a part of the requested time range',
    disableOutsideWorkingHoursForResource: 'It is not possible to reserve {{resourceName}} outside working hours',
    disablePast: 'It is not possible to reserve for the time period, which is entirely in the past',
    recurringConflictsDetected: 'Recurring event conflicts detected ({{conflicted}}/{{total}})',
    recurringMeetingsNotAllowedForResource: 'Recurring meetings are not allowed for {{resourceName}}',
    recurringEventOccurrencesOverlap:
      'Recurring event instances overlap. Shorten duration or increase repeat interval.',
    recurringEventRequiresEndDateWithinDays:
      'Recurring event for {{resourceName}} requires an end date within {{days}} days',
    recurringEventExceedsBookingWindow: 'Recurring event for {{resourceName}} exceeds booking window ({{days}} days)',
    recurringEventRequiresEndWithinOccurrences:
      'Recurring event for {{resourceName}} requires an end within {{occurrences}} occurrences',
    recurringEventExceedsMaxOccurrences:
      'Recurring event for {{resourceName}} exceeds the maximum number of occurrences ({{occurrences}})',
    recurringEventScopeoutAppliedDays: 'Recurring event exceeds limit and will be scoped to {{days}} days.',
    recurringEventScopeoutAppliedOccurrences:
      'Recurring event exceeds limit and will be scoped to {{occurrences}} occurrences.',
    recurringEventOverLimitIgnoredDaysWarning:
      'Recurring event exceeds {{days}} days and may be rejected by the resource provider.',
    recurringEventOverLimitIgnoredOccurrencesWarning:
      'Recurring event exceeds {{occurrences}} occurrences and may be rejected by the resource provider.',
    eventsToConfirm: 'Events to confirm',
    noEventsToConfirm: 'No events to confirm',
    toNavigation: 'To navigation',
    openInNavigation: 'Show in navigation',
    navigateTo: 'Navigate to',
    navigateFrom: 'Navigate from',
    all: 'All Reservations',
    allNews: 'All news',
    from: 'From',
    to: 'To',
    resourceName: 'Location name',
    attendees: 'Attendees',
    state: 'Show events by resource status',
    clearAllFilters: 'Clear all filters',
    clear: 'Clear',
    reservations: 'Reservations',
    filters: 'Filters',
    confirmationRequiredUntil: 'Confirmation of {{resourceName}} is required until {{until}}',
    confirmationRequiredBetween: 'Confirmation of {{resourceName}} is required between {{from}} and {{to}}',
    confirmationPanelRequiredBetween:
      'Confirmation of {{resourceName}} on the panel is required between {{from}} and {{to}}',
    confirmationSensorRequiredBetween:
      'Confirmation of {{resourceName}} automatically by sensor is required between {{from}} and {{to}}',
    confirmationSensorOrPanelRequiredBetween:
      'Confirmation of {{resourceName}}  on the panel or automatically by sensor is required from between {{from}} and {{to}}',
    confirmed: 'Confirmed {{resourceName}} at  {{when}} by {{who}}',
    unconfirmed: 'Unconfirmed',
    confirmedEveryDay: 'Confirmed every day',
    confirmedNever: 'Never confirmed',
    confirmedSomeDay: 'Confirmed some days',
    confirmedToday: 'Confirmed today',
    showCanceled: 'Show also canceled events',
  },
  guest: {
    text: 'Guests are not allowed to use CUE Assist, contact your system administrator, to import you as legit user',
    logout: 'Logout',
  },
  navigation: {
    setAsStart: 'Set as start',
    setAsTarget: 'Set as target',
    changeRoute: 'Choose a new route',
    navigationChangeRouteFromDetail: 'Do you want to change the route according to',
    navigationInProgress: 'Navigation is in progress',
    navigationInProgressDescription: 'We do not recommend entering areas that are not part of your current route.',
    navigationInProgressMultipleRoutes: 'Multiple routes option',
    navigationInProgressMultipleRoutesDescription: 'Choose from multiple route options',
    continueWithRouteNr: 'Continue on route number',
    continueInNavigation: 'Continue in navigation',
    calculatingPath: 'Finding the best route',
    resetAfter: 'RESET AFTER',
    confirmType: 'CONFIRM MY LOCATION',
    finish: 'Finish',
    toNextArea: 'To next area',
    placeholder: 'Search or select location...',
    placeholderResourceList: 'Search locations, subject, organizer...',
    displayName: 'Display name',
    location: 'Location',
    actions: 'Actions',
    categories: 'Categories',
    restartNavigation: 'Reset navigation',
    finished: 'Finish',
    arrivedToDest: 'You have arrived to your destination!',
    close: 'Close',
    newReservation: 'New Reservation',
    startStepByStepNav: 'Start step-by-step navigation',
    backToNavOverviewMap: 'Back to route overview',
    notEnabled: 'Navigation is not enabled',
    notValidKiosk: 'Invalid kiosk',
    notValidKioskLicence: 'Invalid kiosk licence',
    clearTypeFilter: 'type',
    clearCategoryFilter: 'category',
    clearAreaFilter: 'area',
    sortNearest: 'From the nearest',
    sortABC: 'Alphabetically',
    categoriesFilters: 'Categories',
    typesFilters: 'Types',
    areaFilters: 'Areas',
    confirmFilters: 'Confirm',
    cancelFilters: 'Cancel',
    filters: 'Filters',
    youAreHere: 'YOU ARE HERE',
    navigateTo: 'Navigate to',
    showRoute: 'Show route',
    description: 'Description:',
    pois: 'POIs',
    selectAsStart: 'select as start',
    navigationTo: 'Navigation to',
    routeOverview: 'Route overview',
    closeRoute: 'Close route',
    backTo: 'Back to',
    to: 'to',
    openDetail: 'open detail',
    searchLocation: 'Search for location',
    barrierFree: 'Prefer barrier-free paths',
    noBarrierFreeFoundTitle: 'No barrier-free route',
    noBarrierFreeFoundText: 'No barrier-free route has been found.',
    noBarrierFreeFoundQuestion: 'Would you like to view a barrier route?',
    changeStart: 'Change start',
    changeTarget: 'Change target',
    barrierRoute: 'Barrier route',
    scanQrCode: 'Scan QR code',
    scanAndOpenUrlInMobile: 'Scan and open website in your mobile.',
    mapItemDetailNotFoundHeader: 'Not found',
    mapItemDetailNotFoundText: 'Error 404 - not found',
    interactiveView: 'Interactive view',
    atLeastOneUnitIsNotAvailable: 'Some of the Monitor & Control data is not available now.',
    change: 'Change',
    activeFilters: 'active',
    drag: 'Drag',
    noMap: 'Map is not available',
    orientationNotice: 'Turn your device vertically to use the app.',
    share: 'Share',
    copiedOk: 'Copied route',
    copiedError: 'Cannot copied route',
    serverDown: 'Server is currently not available.',
    invalidCueLicence: 'Invalid CUE license',
    reserveMe: 'Reserve me',
  },
  profile: {
    alias: 'Provider',
    paired: 'Paired',
    email: 'Email',
    unpair: 'Unpair account',
    linkedAccounts: 'Linked accounts',
    settings: 'Settings',
    version: 'Version',
    tabGeneral: 'General',
    tabIntegrations: 'Integrations',
    tabSecurity: 'Password',
    tabError: 'Error reporting',
    securityTitle: 'Password settings',
    securityDescription: 'Set or change your password for login.',
    currentPassword: 'Current password',
    newPassword: 'New password',
    confirmNewPassword: 'Confirm new password',
    savePassword: 'Save password',
    headingProfile: 'Profile',
    headingProfileDetails: 'Profile details',
    profileName: 'Name',
    profileSurename: 'Surname',
    profileAddress: 'Address',
    profileEmail: 'E-mail',
    language: 'Language',
    integrationsLinked: 'Linked',
    integrationsNotLinked: 'Not-linked',
    integrationsConnected: 'You are connected!',
    integrationsNotConnected: 'You are not connected.',
    integrationsButtonDisconnect: 'DISCONNECT',
    integrationsButtonConnect: 'CONNECT',
    integrationsButtonSave: 'SAVE',
    integrationsText:
      'Connect CUE Assist to a variety of third-party providers. You can connect it to various online meeting providers such as Zoom or Webex. More integrations will be added in the future.',
    tabOverview: 'Overview',
    tabAccount: 'Account',
    tabAuthentication: 'Authentication',
    tabPreferences: 'Preferences',
    welcome: 'Welcome, {{name}}',
    overviewDescription: 'A quick overview of your account and sign-in methods.',
    viewProfile: 'View profile',
    manageAccounts: 'Manage accounts',
    linkedAccountsCount: 'Calendar providers connected: {{count}}',
    manageAuthentication: 'Manage authentication',
    openPreferences: 'Open preferences',
    accountDescription: 'Your profile details and connected calendar identities.',
    displayName: 'Display name',
    username: 'Username',
    usingPhotoFrom: 'Using photo from {{provider}}',
    usingCustomPhoto: 'Using a custom photo',
    uploadPhoto: 'Upload your own',
    removePhoto: 'Remove',
    profilePhotoTooLarge: 'The profile photo can be at most 5 MB.',
    profileUpdateFailed: 'The profile could not be updated. Please try again.',
    linkedAccountsDescription:
      'Connect calendars from different providers. Your primary account is used for new events by default.',
    connectAccount: 'Connect account',
    noLinkedPhoto: 'Do not use a linked account photo',
    noPrimaryAccount: 'Do not use a primary account',
    accountAlreadyLinked: 'This account is already linked. The same account cannot be added again.',
    accountPairingFailed: 'The account could not be linked. Please try again.',
    useAsProfilePhoto: 'Use as profile photo',
    usedAsProfilePhoto: 'Used as profile photo',
    lastActivity: 'Last activity',
    connectedSince: 'Connected since',
    disconnect: 'Disconnect',
    connected: 'Connected',
    notConnected: 'Not connected',
    primary: 'Primary',
    usePrimary: 'Use as primary calendar account',
    calendarDataInfo:
      'Calendar data stays with its provider. CUE only reads the details required to coordinate your schedule.',
    onlineMeetingsTitle: 'Online meeting integrations',
    authenticationDescription: 'Manage the ways you sign in and identify yourself on a panel.',
    emailLogin: 'Email login',
    emailLoginDescription: 'Sign in using a secure link sent to your email.',
    enabled: 'Enabled',
    disabled: 'Disabled',
    password: 'Password',
    set: 'Set',
    notSet: 'Not set',
    passwordSetDescription: 'You can sign in with your password.',
    passwordNotSetDescription: 'No password is currently set.',
    changePassword: 'Change password',
    setPassword: 'Set password',
    pin: 'PIN',
    pinNotSetDescription: 'No PIN is currently set.',
    generatePin: 'Generate PIN',
    pinRequestSent: 'The new PIN is now active.',
    pinRequestFailed: 'The PIN could not be generated or activated. Please try again.',
    pinRequestLimited: 'You can generate five PINs in 15 minutes. Please try again later.',
    newPin: 'New PIN',
    confirmPin: 'Save this PIN',
    generateNewPin: 'Generate a new PIN',
    generateAnotherPin: 'Generate another',
    deletePin: 'Delete PIN',
    deletePinConfirmation: 'Delete your PIN? You will no longer be able to use it to identify yourself on a panel.',
    pinDeleted: 'Your PIN has been deleted.',
    pinDeleteFailed: 'The PIN could not be deleted. Please try again.',
    pinGenerationDescription: 'PINs are generated to uniquely identify you on a panel.',
    pinConfirmationDescription: 'This PIN is not active yet. Confirm within 15 minutes to replace your current PIN.',
    linkedPhotoUnavailable: 'Sign in with this account to load its photo, if the provider supplies one.',
    nfcCard: 'NFC card',
    neverUsed: 'Never used',
    managedByAdministrator: 'Managed by administrator',
    preferencesDescription: 'Personalize reservations, language, and the locations you see.',
    reservationsDescription: 'Set the default reminder for new reservations.',
    localeDescription: 'Control the language and calendar conventions.',
    homeArea: 'Home area',
    filteredAreasSelected: '{{count}} selected',
    eventReminders: 'Event reminders',
    diagnostics: 'Diagnostics & error reporting',
  },
  webex: {
    loadFailed: 'Webex settings could not be loaded. Please try again.',
    saveFailed: 'Webex settings could not be saved. Your changes have been kept. Please try again.',
    maxLengthError: 'Maximum length: {{length}} characters.',
    defaultSettingsHeading: 'Default meeting settings',
    defaultSettingsDescription: 'You can use following placeholders in text inputs:',
    defaultSettingsInputTitle: 'Title',
    defaultSettingsInputTitleDescription: 'Will be used as Webex Online Meeting Title. Max. 128 characters',
    defaultSettingsInputTitleErrorMandatory: 'This field is mandatory!',
    defaultSettingsInputDescription: 'Description',
    defaultSettingsInputDescriptionDescription: 'Will be used as Webex Online Meeting Agenda. Max. 1200 characters',
    defaultSettingsInputPassword: 'Password',
    defaultSettingsInputPasswordDescription: 'If left empty, password will be generated by Webex',
    defaultSettingsInputPublic: 'Public',
    defaultSettingsInputPublicDescription: 'Is the meeting public?',
    defaultSettingsInputSendemail: 'Send Webex Meeting e-mail',
    defaultSettingsInputSendemailDescription:
      'Do you want to send Webex e-mail to attendees when a meeting is created?',
    defaultSettingsInputBody: 'Body',
    defaultSettingsInputBodyDescription:
      'This part will be attached to Calendar Meeting BODY. Use this to attach Webex meeting info to main email sent to attendees.',
    notPaired:
      'You have not paired your Webex account yet, go to your profile settings, select tab Integrations and open panel Webex.',
  },
  orders: {
    onlyWithEvent: 'It is not possible to add any request to this resource without an event',
    notAvailable: 'It is not possible to add any request to this resource',
    saveOnly: 'Save',
    saveOnlyAndOrder: 'Save & Order',
    created: 'Created',
    eventStart: 'Event start',
    loadMore: 'Load more',
    list: 'List',
    floorplan: 'Floorplan',
    orderButton: 'Request service',
    reserveOnly: 'Reserve',
    changeOnly: 'Change',
    reserveAndOrder: 'Reserve & Order',
    changeAndOrder: 'Change & Order',
    newOrder: 'Order',
    newRequest: 'Request',
    newOrderTo: 'Request service to',
    newOrderToEvent: 'Order to',
    orders: 'Orders',
    serviceRequests: 'Service requests',
    orderNewTitle: "Select request's location",
    filterResources: 'Locations',
    filterServices: 'Items',
    filterEventOrders: 'Orders',
    filterResourceOrders: 'Service requests',
    filterEventStatuses: 'Show orders by event status',
    countOrderedServices: '{{count}} ordered items',
    note: 'Note',
    newOrderTitle: 'New order',
    newServiceInstruction: 'To create new service request please add at least one item',
    newServiceTitle: 'New service request',
    newServiceRequest: 'New service request',
    newOrderInstruction: 'To create new order please add at least one Order item',
    addItem: 'Add item',
    noDescription: 'No description',
    minimalLength: 'Please enter a minimum of {{length }} character.',
  },
  wayfinding: {
    changeButton: 'change',
    directionsWindowHeading: 'Directions',
    goBack: 'Go back',
    titleScanQr: 'SCAN THE QR CODE TO SELECT YOUR STARTING LOCATION',
    buttonTypeQr: 'TYPE QR',
    descriptionTypeQr:
      'Type {{digitCodeNumbers}}-digit code you can find next to the QR code to set the starting location for the navigation',
    buttonSearch: 'SEARCH',
    buttonScanQr: 'SCAN QR',
    buttonRefineLocation: 'New Start',
    buttonRestart: 'Reset',
    buttonClose: 'Close',
    buttonList: 'Select from list',
    continuousLeft: 'Keep going to the <strong>left</strong>',
    continuousSuperLowLeft: 'Keep going to the <strong>very slightly left</strong>',
    continuousStraight: 'Keep going <strong>straight</strong>',
    continuousRight: 'Keep going to the <strong>right</strong>',
    continuousLowLeft: 'Keep going to the <strong>slightly left</strong>',
    continuousSuperLeft: 'Keep going to the <strong>absolutely left</strong>',
    continuousLowRight: 'Keep going to the <strong>slightly right</strong>',
    continuousSuperRight: 'Keep going to the <strong>absolutely right</strong>',
    Left: 'turn <strong>left</strong>',
    SuperLowLeft: 'turn <strong>very slightly left</strong>',
    SuperLowRight: 'turn <strong>very slightly right</strong>',
    Straight: 'go <strong>straight</strong>',
    Right: 'turn <strong>right</strong>',
    LowLeft: 'turn <strong>left</strong>',
    SuperLeft: 'turn <strong>absolutely left</strong>',
    LowRight: 'turn <strong>right</strong>',
    SuperRight: 'turn <strong>absolutely right</strong>',
    toQrCode: ' to the <strong>QR-{{code}} </strong>',
    isLast: ' to the target destination.',
    normal:
      'Keep going for <strong>{{distance}}m</strong>, reach <strong>{{areaMetricName}}</strong>, escape to the <strong>{{startPortalName}}</strong> and move to the area <strong>{{endPortalName}}</strong>.',
    normalNext:
      'Keep going straight for <strong>{{distance}}m</strong> {{qrCodeText}} {{portalText}},then {{angleText}}',
    continuous: '{{text}} {{portalText}}, continue in this direction for <strong>{{distance}}m</strong>{{qrCodeText}}',
    toQrCodeContinuous: ' you will see <strong>the QR-{{code}}</strong>',
    nextAngle: 'Then - {{angleText}}',
    codeNotFound: 'The QR code has not been found.',
    server500: 'Server error. Try again later.',
    notFound: 'Route not found',
    barrierFreenotFound: 'Barrier free route not found',
    startNotFound: 'Start not found',
    targetNotFound: 'Target not found',
    startAndTargetNotFound: 'Start and Finish not found',
    continueWithWebNav: 'Continue with mobile app',
    restartHeading: 'Do you want to reset navigation?',
    sameStartAsTargetTitle: 'The same start and target',
    sameStartAsTarget: 'You have entered the same start and target.',
    sameStartAsTargetDescription: 'Change one of the navigation points.',
    sameStartAsTargetAsk: 'Would you like to change the route?',
    routeNotExisting: "Unfortunately we can't find navigation for your route.",
    routeNotExistingDescription: 'Check your route and change one of the navigation points.',
    restartDescription: "You'll lose all navigation progress. Start and finish will be deleted.",
    or: 'or...',
    orCategory: 'or choose the category',
    orCategoryTempText: 'or to the nearest of the category',
    selectStartLocation: 'Select start location',
    selectTargetLocation: 'Select target location',
    start: 'Start',
    target: 'Target',
    touchMe: 'Touch me',
    searched: 'Searched',
  },
  errorReporting: {
    clearLogs: 'Clear Logs',
    download: 'Download',
    errorReportingHeading: 'Error reporting',
    panelbarErrors: 'Errors',
    panelbarUnprocessedEvents: 'Unprocessed events',
  },
  notFound: {
    heading: 'Page not found',
    dashboardButton: 'GO TO DASHBOARD',
    resourceNotFound: 'Location has not been found',
    orderNotFound: 'This reservation has no orders',
  },
  pager: {
    visible: 'Visible:',
    fromCount: 'from {{total}} items total',
  },
  reservationOption: {
    timelineDescription: 'A grid of squares to represent occupancy of rooms',
    floorplansDescription: "Graphical representation of a building's floorplan",
    dialogTitle: 'Dialog',
    dialogDescription: 'Open the reservation form without a preselected location',
  },
  outlookAddin: {
    cannotFetchError: 'Cannot fetch data about event from the calendar server.',
    cannotFetchErrorHint: 'This usually happens when the Outlook user is different than Addin logged user?',
    errorDescription: 'Error has occured during the communication with the server',
    refresh: 'Refresh',
    userIsLoggedIn: 'The user has been authorised. You can close this window.',
    loginUserInfo: 'Welcome, please login to continue.',
    login: 'Login',
    logout: 'Logout',
    noResourceOnEvent: 'No locations found in the event.',
    chooseResource: 'Add to event',
    activeFilterCount: 'active filter groups',
    removeResource: 'Remove from event',
    openFilters: 'open filters',
    closeFilters: 'close filters',
    resetFilters: 'reset',
    onlyAvailable: 'Only available',
    allResources: 'All locations',
    cueExternalLink: 'Link to CUE Assist',
    cueSearch: 'Open detail',
    orders: 'Orders',
    locationDetail: 'Location detail',
    assignedMine: 'Assigned to me',
    assignedOurs: 'Assigned to us',
    assignedRestricted: 'Restricted',
    assignedFree: 'No restrictions',
    availability: 'Availability',
    available: 'Available',
    unavailable: 'Unavailable',
    inThisEvent: 'In this event',
    resourceCannotBeSelected: '{{resource}} cannot be added to the event.',
    resourceCannotBeSelectedQuestion:
      '{{resource}} does not meet availability restrictions. Add it to the event anyway?',
    resourceCannotBeSelectedDueToOccupiedResourceType:
      'Only one resource of this type can be reserved. Do you want to keep {{resource}}?',
    resourceCannotBeSelectedDueToOccupiedResourceTypeQuestion:
      'Another resource of the same type as {{resource}} is already selected or reserved, and multiple reservations of this type are not allowed. Add it to the event anyway?',
    resourcesRemovedFromEvent: 'Removed from event',
    resourcesRemovedDueRestrictions: 'Removed from event: {{resources}}',
    resourcesShouldBeRemovedDueRestrictions:
      'Resource {{resource}} does not meet restrictions. Remove it from the event?',
    confirmationYes: 'Yes',
    confirmationNo: 'No',
    confirmationKeep: 'Keep',
    confirmationRemove: 'Remove',
    unavailabilityReasonMinEventDuration: 'Min event duration is {{duration}}',
    unavailabilityReasonMaxEventDuration: 'Max event duration is {{duration}}',
    unavailabilityReasonMaxDaysAdvance: 'Can be reserved up to {{days}} {{dayUnit}} in advance',
    unavailabilityReasonDayZero: 'days',
    unavailabilityReasonDayOne: 'day',
    unavailabilityReasonDayTwo: 'days',
    unavailabilityReasonDayFew: 'days',
    unavailabilityReasonDayMany: 'days',
    unavailabilityReasonDayOther: 'days',
    unavailabilityReasonOutsideWorkingHours: 'Outside working hours',
    unavailabilityReasonReserved: 'Resource is reserved at this time',
    unavailabilityReasonEventEndsInPast: 'Event cannot end in the past',
    unavailabilityReasonMultipleResourceTypeReservationsNotAllowed:
      'Another resource of this type is already selected for the event',
    changeLocation: 'Change location',
    resourceRestricted: 'This location is restricted.',
    chooseAnotherResource: 'Please choose another location',
    noSubject: 'No subject',
    selectAtLeastOneResourceTip: 'Select at least one resource to load events.',
    failedToLoadEvents: 'Failed to load events.',
    differentOutlookAndAddinUser: 'The user in Outlook and CUE Addin must be the same',
    onlyAvailibleResourcesNotFound: 'No available location found',
    resourcesNotFound: 'No location found',
    cannotLoginUser: 'User cannot be logged in. Try it again.',
  },
  resource: {
    name: 'Name',
    alternativeName: 'Alternative name',
  },
  multiview: {
    poiNotFound: 'POI has not been found',
    resourceNotFound: 'Resource has not been found',
    navigationNotAvailable: 'This object is not available in navigation',
    tabs: {
      login: 'Login',
      device: 'Device',
      control: 'Control',
      news: 'News',
      requests: 'Requests',
      events: 'Events',
      navigation: 'Navigation',
      detail: 'Detail',
    },
    more: {
      description: 'You are not logged in - to show more possible options, please login in the form below',
    },
    events: {
      myReservationsTitle: 'My reservations',
      myReservationsSubtitle: '+24 hours',
      noReservations: 'You have no reservations in the next 24 hours.',
    },
  },
  webapps: {
    welcome: 'Welcome to',
    beginStep: 'To begin, link it to your Cue environment.',
    deviceReadyToConnect: 'This device is ready to be connected with your CUE Solution software.',
    connectInAdmin: 'Connect in CUE Admin',
    stepOpenAdmin: 'Open CUE Admin',
    stepClients: 'Navigate to Units → Clients',
    stepSelectNewDevice: 'Select NEW to add this device',
    stepDeviceName: 'Name the device and type the code',
    connect: 'Connect',
    deviceCode: 'Device code',
    codeValidity: 'Code validity',
    reserve: 'Reserve',
    scanQrToReserveViaCueAssist: 'Scan QR to reserve via CUE Assist',
  },
  reservationPanelApp: {
    resourceDetail: 'Resource detail',
    calendar: 'Calendar',
    serviceRequest: 'Service request',
    control: 'Control',
    reserved: 'Reserved',
    free: 'Free',
    occupied: 'occupied',
    reserve: 'Reserve:',
    peopleCount: 'people count',
    confirmTill: 'Confirm (till {{time}})',
    extend: 'Extend ({{minutes}} min)',
    finish: 'Finish',
    confirm: 'Confirm',
    confirmationRequired: 'Confirmation is required till {{time}}!',
    userVerification: 'User verification',
    pinCode: 'PIN code:',
    minutes30: '30 min',
    minutes60: '60 min',
    minutes90: '90 min',
    till: 'till',
    back: 'Back',
    availableServices: 'Available services',
    collapseAll: 'Collapse all',
    expandAll: 'Expand all',
    loadingServices: 'Loading services...',
    failedToLoadServices: 'Failed to load services.',
    noServicesAvailable: 'No services available.',
    serviceCount: '{{count}} services',
    serviceImageAlt: 'Service',
    notesAllowed: 'Notes allowed',
    multipleItems: 'Multiple items',
    unavailable: 'Unavailable',
    addNote: 'Add note',
    addSummaryNote: 'Add summary note',
    creatingRequest: 'Creating request...',
    newRequest: 'New request',
    requestCouldNotBeCreated: 'Request could not be created.',
    serviceRequestCreated: 'Service request created',
    redirectingToMainPageInSeconds: 'Redirecting to the main page in {{seconds}} seconds.',
  },
  analytics: {
    noAnalyticsResData: 'The necessary data for displaying dashboards with statistics does not yet exist.',
    satisfactionRatingsFrom: 'From',
    satisfactionRatingsTo: 'To',
  },
  monitorAndControl: {
    selectArea: 'Select area',
    infoPanel: 'Selection info ',
    showMap: 'Show map',
    allowedValueRange: 'Allowed range: {{range}}',
    valueOutsideAllowedRange: 'The value could not be set because it is outside the allowed range.',
  },
  cardServiceApp: {
    appTitle: 'Card Service App',
    help: 'Help',
    clearHistory: 'Clear history',
    registered: 'Registered',
    loadHistory: 'Load history',
    lastLoad: 'Last load:',
    assigned: 'Assigned',
    adminLoginRequired: 'To use the application, you must log in as an administrator.',
    scanToLogin: 'Scan by your mobile and log in:',
    or: 'or',
    placeNfcCard: 'Place your NFC card in a designated area:',
    enterPin: 'Enter PIN:',
  },
};
